import React, { useEffect, useRef, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { useHeadphones, takeOffHeadphones } from '../state/headphones';
import { ROOMS } from '../rooms';
import { api } from '../services/api';
import { audioEngine } from '../services/audioEngine';
import { takeAudio, releaseAudio, onAudio } from '../services/audioPool';
import { setLevel } from '../city/hearing';
import { edgeInset } from '../config';
import { useStackSlot, DOCK_MAX_H } from './hudStack';

// HeadphonesDock — what plays in the headphones you put on at a table
// (2026-10-31). Khari: "they see a list of recordings that play just for them.
// The music stops when the headphones are taken off. This way, we do not lose
// any tracks, and they have a way to be showcased."
//
// The list is every spoken-word piece performed in the city, newest first (API
// GET /api/world/spoken-word, SpokenWordService.headphonesList). Pick one and it
// plays from the top, for you only, on its own player (not the room's, not
// synced: it is a recording, yours to start); when it ends the next one plays,
// after GAP_MS of silence (Khari, 2026-11-02: "Give the tracks about 3 seconds of
// silence between plays") — the next row reads "Next" while it waits.
// "Take them off" — or walking away from the table, or leaving the room — stops
// it at once. While they are on, the room is held quiet under them: the outside
// radio (audioEngine holder 'headphones') and a reading upstairs (VenueStage
// ducks under `wearingHeadphones`).
//
// In the right-hand column like the other docks (hudStack); putting them on
// closes the counter's dock, so the two never stack.

const ACCENT = '#ffc27a';
const line = (a) => `rgba(255,194,122,${a})`;
const HOLDER = 'headphones';
const LEVEL = 0.95;
const LIST_MS = 5 * 60000;
const GAP_MS = 3000;   // silence between one piece and the next
let cache = { at: 0, pieces: null };

const mmss = (ms) => { const s = Math.max(0, Math.round((ms || 0) / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };

const wrap = {
  position: 'fixed', right: edgeInset(16, 'right'), bottom: 'calc(max(16px, env(safe-area-inset-bottom)) + var(--hud-player-h, 0px))',
  zIndex: 70, width: 'min(340px, calc(100vw - 32px))', maxHeight: DOCK_MAX_H(440),
  display: 'flex', flexDirection: 'column', pointerEvents: 'auto', fontFamily: 'system-ui, sans-serif',
  background: 'rgba(16,10,6,0.95)', border: `1px solid ${line(0.36)}`, borderRadius: 16,
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
};

export default function HeadphonesDock() {
  const on = useHeadphones((s) => s.on);
  const interior = useWorldStore((s) => s.interior);
  const slot = useStackSlot();
  const [pieces, setPieces] = useState(cache.pieces);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(null);   // { id, t } t = position ms (for the bar)
  const el = useRef(null);
  const gap = useRef(null);   // the pause before the next piece (cleared by any stop or pick)
  const listRef = useRef(pieces);
  listRef.current = pieces;

  // Leaving the room takes them off.
  useEffect(() => { if (on && interior !== on.zone) takeOffHeadphones(); }, [on, interior]);

  const stop = () => {
    clearTimeout(gap.current); gap.current = null;
    if (el.current) { try { el.current.pause(); } catch { /* gone */ } releaseAudio(el.current); el.current = null; }
    setPlaying(null);
  };

  const play = (p) => {
    stop();
    const a = takeAudio(p.url);
    el.current = a;
    setLevel(a, LEVEL);
    try { a.currentTime = 0; } catch { /* on metadata */ }
    onAudio(a, 'ended', () => {
      if (el.current !== a) return;
      const list = listRef.current || [];
      const i = list.findIndex((x) => x.id === p.id);
      const next = list[i + 1];
      if (!next) { stop(); return; }
      setPlaying({ id: next.id, t: 0, waiting: true });
      gap.current = setTimeout(() => { gap.current = null; if (el.current === a) play(next); }, GAP_MS);
    });
    a.play().catch(() => { if (el.current === a) setPlaying(null); });
    setPlaying({ id: p.id, t: 0 });
  };

  // On: the list (cached a few minutes), the room held quiet, the counter's dock closed. Off: everything stops.
  useEffect(() => {
    if (!on) return undefined;
    useWorldStore.getState().closeShop?.();
    audioEngine.roomQuiet(true, 500, HOLDER).catch?.(() => {});
    if (!cache.pieces || Date.now() - cache.at > LIST_MS) {
      setFailed(false);
      api.spokenWord().then((d) => {
        const list = Array.isArray(d?.pieces) ? d.pieces : [];
        cache = { at: Date.now(), pieces: list };
        setPieces(list);
      }).catch(() => setFailed(true));
    }
    return () => {
      stop();
      audioEngine.roomQuiet(false, 700, HOLDER).catch?.(() => {});
    };
  }, [on]); // eslint-disable-line react-hooks/exhaustive-deps

  // The progress bar.
  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => { const a = el.current; if (a) setPlaying((p) => (p && !p.waiting ? { ...p, t: (a.currentTime || 0) * 1000 } : p)); }, 500);
    return () => clearInterval(t);
  }, [playing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!on) return null;
  const room = ROOMS[on.zone]?.name || 'the café';
  return (
    <div ref={slot} style={wrap} role="dialog" aria-label="Headphones: spoken word recordings">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 13px', borderBottom: `1px solid ${line(0.18)}` }}>
        <div>
          <div style={{ color: '#fff3e2', font: '700 14.5px system-ui' }}>🎧 Spoken word</div>
          <div style={{ color: ACCENT, font: '11px system-ui', letterSpacing: 0.4, opacity: 0.85 }}>Pieces performed at {room}, just for you</div>
        </div>
        <button
          onClick={() => takeOffHeadphones()}
          style={{ cursor: 'pointer', background: line(0.12), color: ACCENT, border: `1px solid ${line(0.4)}`, borderRadius: 999, padding: '6px 12px', font: '700 12.5px system-ui' }}
        >Take them off</button>
      </div>
      <div style={{ overflowY: 'auto', padding: '6px 6px 10px' }}>
        {!pieces && !failed && <div style={{ padding: 14, color: 'rgba(255,243,226,0.6)', font: '13px system-ui' }}>Finding the recordings…</div>}
        {failed && <div style={{ padding: 14, color: '#ff9a9a', font: '13px system-ui' }}>Couldn&apos;t load the recordings. Take them off and try again.</div>}
        {pieces && !pieces.length && <div style={{ padding: 14, color: 'rgba(255,243,226,0.7)', font: '13px/1.5 system-ui' }}>Nothing recorded yet. Every piece performed in the corner upstairs ends up here.</div>}
        {(pieces || []).map((p) => {
          const me = playing?.id === p.id;
          return (
            <button
              key={p.id}
              onClick={() => (me ? stop() : play(p))}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', cursor: 'pointer',
                padding: '8px 9px', margin: '2px 0', borderRadius: 10, border: 'none',
                background: me ? line(0.14) : 'transparent', color: '#fff3e2',
              }}
            >
              <span style={{ width: 34, height: 34, flex: 'none', borderRadius: 8, overflow: 'hidden', background: line(0.12), display: 'grid', placeItems: 'center', color: ACCENT, fontSize: 14 }}>
                {p.art ? <img src={p.art} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (me ? '❚❚' : '▶')}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', font: '600 13.5px system-ui', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{me ? (playing.waiting ? 'Next · ' : '▶ ') : ''}{p.title}</span>
                <span style={{ display: 'block', font: '11.5px system-ui', color: 'rgba(255,243,226,0.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.artist} · {mmss(p.durMs)}</span>
                {me && <span style={{ display: 'block', height: 2, marginTop: 5, borderRadius: 2, background: ACCENT, width: `${Math.min(100, (playing.t / p.durMs) * 100)}%` }} />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
